import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useForm } from 'react-hook-form';
import { TextFieldWithController } from '../TextField';
import { SelectWithController } from '../SelectMenus';
import BasicSignupFields from './BasicSignupFields';
import Button from '../Button';
import ValidationError from '../ValidationError';
import SpacingContainer from '../SpacingContainer';
import {
  getRequiredValidation,
  getUrlValidation,
} from '../lib/validationRules';

const signupFormPropertyTypes = {
  firstNameLabel: PropTypes.string.isRequired,
  lastNameLabel: PropTypes.string.isRequired,
  emailAddressLabel: PropTypes.string.isRequired,
  passwordLabel: PropTypes.string.isRequired,
  emailValidationMessage: PropTypes.string.isRequired,
  requiredValidationMessage: PropTypes.string.isRequired,
  passwordTooShortMessage: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  formatEmailSuggestion: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  serverValidation: PropTypes.node,
  submitDisabled: PropTypes.bool,
  companyNameLabel: PropTypes.string,
  websiteUrlLabel: PropTypes.string,
  chamberOfCommerceNumberLabel: PropTypes.string,
  languageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
  urlValidationMessage: PropTypes.string,
  CountryField: PropTypes.elementType,
};

type SignupFormProperties = InferProps<typeof signupFormPropertyTypes> & {
  submitDisabled?: boolean;
};

interface SignupFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName?: string;
  websiteUrl?: string;
  chamberOfCommerceNumber?: string;
  country?: string;
  language?: string;
}

const SignupForm = ({
  firstNameLabel,
  lastNameLabel,
  emailAddressLabel,
  passwordLabel,
  requiredValidationMessage,
  emailValidationMessage,
  passwordTooShortMessage,
  submitLabel,
  formatEmailSuggestion,
  submitHandler,
  submitDisabled,
  serverValidation,
  companyNameLabel,
  websiteUrlLabel,
  chamberOfCommerceNumberLabel,
  languageOptions,
  urlValidationMessage,
  CountryField,
}: SignupFormProperties) => {
  const form = useForm<SignupFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      companyName: companyNameLabel ? '' : undefined,
      websiteUrl: websiteUrlLabel ? '' : undefined,
      chamberOfCommerceNumber: chamberOfCommerceNumberLabel ? '' : undefined,
      language: languageOptions ? '' : undefined,
      country: '',
    },
  });

  const { handleSubmit, errors, control } = form;

  const handleWebsiteUrlFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    form.setValue(
      'websiteUrl',
      event.target.value === '' ? 'https://' : event.target.value,
    );
  };

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(submitHandler)}
    >
      <BasicSignupFields
        firstNameLabel={firstNameLabel}
        lastNameLabel={lastNameLabel}
        emailAddressLabel={emailAddressLabel}
        passwordLabel={passwordLabel}
        requiredValidationMessage={requiredValidationMessage}
        emailValidationMessage={emailValidationMessage}
        passwordTooShortMessage={passwordTooShortMessage}
        formatEmailSuggestion={formatEmailSuggestion}
        control={control}
        errors={errors}
      />

      { companyNameLabel && (
      <div className="form-item">
        <TextFieldWithController
          name="companyName"
          control={control}
          rules={getRequiredValidation(requiredValidationMessage)}
          variant="filled"
          id="signup-buyer-company-name"
          label={companyNameLabel}
        />
        <ValidationError />
      </div>
      )}

      { websiteUrlLabel && (
        <div className="form-item">
          <TextFieldWithController
            name="websiteUrl"
            control={control}
            rules={getUrlValidation(
              urlValidationMessage ?? '',
              requiredValidationMessage,
            )}
            variant="filled"
            id="signup-buyer-website"
            label={websiteUrlLabel}
            onFocus={handleWebsiteUrlFocus}
          />
          <ValidationError errors={errors} name="websiteUrl" />
        </div>
      )}

      { chamberOfCommerceNumberLabel && (
        <div className="form-item">
          <TextFieldWithController
            control={control}
            name="chamberOfCommerceNumber"
            rules={getRequiredValidation(requiredValidationMessage)}
            variant="filled"
            id="signup-buyer-chamber-of-commerce"
            label={chamberOfCommerceNumberLabel}
          />
          <ValidationError errors={errors} name="chamberOfCommerceNumber" />
        </div>
      )}

      {CountryField && (
        <>
          <SpacingContainer />
          <div className="form-item">
            <CountryField
              name="country"
              control={control}
              rules={{}}
              id="signup-buyer-country"
              label="Country"
            />
            <ValidationError />
          </div>
        </>
      )}

      { languageOptions && (
        <>
          <SpacingContainer />
          <div className="form-item">
            <SelectWithController
              name="language"
              control={control}
              rules={{}}
              variant="outlined"
              fullWidth
              label="Language"
              options={languageOptions}
              id="signup-buyer-language"
            />
            <ValidationError />
          </div>
        </>
      )}

      <SpacingContainer />
      { serverValidation }
      <Button
        fullWidth
        variant="contained"
        disabled={submitDisabled}
        className="account-register-submit"
        type="submit"
      >
        {submitLabel}
      </Button>
    </form>
  );
};

SignupForm.propTypes = signupFormPropertyTypes;

export default SignupForm;
